import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import Modal from '../modal/Modal';
import { Col, Row } from '../../lib/utils';
import { Body2Regular, Body3Regular, Subtitle1Bold } from '../../lib/font';
import ColorSquareButton from '../Button/ColorSquareButton';
import WhiteSquareButton from '../Button/WhiteSquareButton';
import { colors } from '../../lib/colors';
import { usePublicMemberList } from '@/features/publicMember/hooks';
import { ICenterMember } from '@/features/publicMember/types';
import AppAccountStatusBadge from '@/components/MemberList/AppAccountStatusBadge';
import { useTicketTransferOptions, useTransferTicket } from '@/features/ticketTransfer/hooks';
import { TicketTransferOption, TicketTransferSessionNumberPolicy, TransferTicketBody } from '@/features/ticketTransfer/types';
import {
    KEEP_COACH_SELECTION,
    TransferCoachSelection,
    buildTransferTicketBody,
    isCoachSelectableOption,
    isCoachSelectionIncomplete,
    makeCoachSelectionKey,
    readCoachSelectionForKey,
    resolveChangedCoachId,
    resolveRecipientCoachSuggestions,
} from '@/features/ticketTransfer/coachSelection';
import { useMemberCoaches } from '@/features/memberCoach/hooks';
import { CodeValueType } from '../../types/types';
import NewTextInputDropDown from '../DropDown/NewTextInputDropDown';
import SvgIcon from '../SvgIcon';
import {
    TICKET_TRANSFER_RETRY_EXHAUSTED_MESSAGE,
    TICKET_TRANSFER_ROLLBACK_INCOMPLETE_BLOCK_MESSAGE,
    TRANSFER_REVIEW_BUSY_NOTICE,
    TRANSFER_REVIEW_INCOMPLETE_NOTICE,
    TRANSFER_REVIEW_REQUIRED_MESSAGE,
    TRANSFER_REVIEW_UNCONFIRMED_NOTICE,
    TRANSFER_REVIEW_UNKNOWN_NOTICE,
    getTicketTransferRollbackState,
    TICKET_TRANSFER_ROLLBACK_INCOMPLETE_MESSAGE,
    TICKET_TRANSFER_STALE_MESSAGE,
    TICKET_TRANSFER_UNCONFIRMED_MESSAGE,
    TICKET_TRANSFER_UNRESOLVED_BLOCK_MESSAGE,
    getTicketTransferErrorMessage,
    getTicketTransferMaxFromError,
} from '@/features/ticketTransfer/errors';
import {
    MAX_SAME_KEY_RETRIES,
    attachClientOperationId,
    classifySameKeyFailure,
    getSameKeyRetryDelayMs,
    isClientOperationIdRejected,
    isClientOperationIdUnsupported,
    isTicketTransferStaleFailure,
    markClientOperationIdUnsupported,
    stripClientOperationId,
} from '@/features/ticketTransfer/operationKey';
import {
    TransferReviewKind,
    clearPendingTransferForOperation,
    clearTransferReview,
    updatePendingTransferForOperation,
    clearTransferReviewForOperation,
    loadPendingTransfer,
    loadTransferReview,
    savePendingTransfer,
    saveTransferReview,
} from '@/features/ticketTransfer/pendingStore';
import {
    TransferRecipientMode,
    getSplitActionLabel,
    isSplitSelectableOption,
} from '@/features/ticketTransfer/sameMemberSplit';
import {
    getTransferLimitNotice,
    getTransferUnavailableNotice,
    isTransferCountWithinLimit,
    resolveMaxTransferableCount,
    resolveUnconsumedFutureCount,
} from '@/features/ticketTransfer/transferLimits';
import { formatKoreanPhoneNumber } from '../../utils/phoneNumber';

type Props = {
    centerId: string;
    memberId: string;
    memberName?: string;
    setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
    createAlert: any;
    onSuccess?: () => void;
    /**
     * 센터의 활성 강사 목록(담당 강사 변경 모달과 같은 소스). PT 양도 시 "다른 강사에게 배정"
     * 선택지에 쓴다. 없으면 강사 변경 없이 기존 양도만 된다.
     */
    coachOptions?: CodeValueType[];
};

const textInputStyle: React.CSSProperties = {
    width: '100%',
    height: 42,
    border: '1px solid #E1E4E8',
    borderRadius: 8,
    padding: '0 12px',
    fontSize: 15,
    outline: 'none',
};

const sectionTitleStyle: React.CSSProperties = {
    fontSize: 15,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 10,
};

const pillStyle: React.CSSProperties = {
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
};

/** 한 번의 제출. 재확인 때도 본문(같은 키)과 안내 문구용 이름을 그대로 쓴다. */
type TransferAttempt = {
    body: TransferTicketBody;
    isSplit: boolean;
    ticketName: string;
    recipientName: string;
    fromCoachName: string;
    toCoachName: string;
    /**
     * 'busy': 상품 분류 일괄 적용과 겹쳐 거절된(409·503) 요청을 같은 키로 다시 보내려고 쥔 것. 서버가 확정 응답을 줬으므로
     * 다시 보내는 것은 「응답 유실 뒤 재확인」이 아니라 같은 키의 새 제출과 같다(분류 때 수동 재확인으로 치지 않는다).
     */
    origin?: 'busy';
};

/** 응답 유실·결과 미확인으로 넘어갈 때는 'busy' 표시를 뗀다(그때부터는 진짜 재확인이다). */
const asUnconfirmedAttempt = (attempt: TransferAttempt): TransferAttempt => {
    const { origin: _origin, ...rest } = attempt;
    return rest;
};

const resolveMemberId = (member: any) => member?.memberId || member?._id || '';

const resolveMemberTypeLabel = (memberType?: string) => {
    if (memberType === 'public') return '퍼블릭 회원';
    if (memberType === 'temp' || memberType === 'original') return '수강회원';
    return memberType || '';
};

const ModalTicketTransfer = ({
    centerId,
    memberId,
    memberName,
    setIsVisible,
    createAlert,
    onSuccess,
    coachOptions,
}: Props) => {
    const [recipientSearch, setRecipientSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedRecipientId, setSelectedRecipientId] = useState('');
    const [selectedRecipientSnapshot, setSelectedRecipientSnapshot] = useState<{
        name: string;
        phoneNumber?: string;
        memberType?: string;
        appLinked?: boolean;
        isInfo?: boolean;
        appAccountStatus?: ICenterMember['appAccountStatus'];
        appWithdrawnAt?: string;
    } | null>(null);
    const [selectedSourceId, setSelectedSourceId] = useState('');
    const [transferCount, setTransferCount] = useState('');
    const [policy, setPolicy] = useState<TicketTransferSessionNumberPolicy>('RESET');
    const [memo, setMemo] = useState('');
    // 양도 수강권의 담당 강사. 기본은 '유지'(= 지금까지의 양도와 완전히 같다).
    // 선택은 "이 수강권 → 이 회원" 맥락 키에 묶어 저장한다. 수강권이나 받는 회원이 바뀌면
    // 키가 달라져 그 즉시 '유지' 로 읽힌다(effect 지연 없이).
    const [storedCoachSelection, setStoredCoachSelection] = useState<{
        key: string;
        selection: TransferCoachSelection;
    } | null>(null);
    const isSubmittingRef = useRef(false);
    // 받는 쪽: 다른 회원(기존 양도) / 같은 회원 · 다른 강사와 회차 나누기.
    const [recipientMode, setRecipientMode] = useState<TransferRecipientMode>('OTHER');
    const isSplit = recipientMode === 'SAME';
    // 같은 키로 다시 확인 중이면 몇 번째인지(화면 안내용). 0 이면 재시도 중이 아니다.
    const [sameKeyRetryCount, setSameKeyRetryCount] = useState(0);
    // 결과를 끝내 확인하지 못한 요청의 원본 수강권. 이 창에서는 그 수강권을 다시 보내지 않는다
    // (새 키로 보내면 첫 요청이 처리됐을 때 두 번째 양도가 된다).
    const [unresolvedSourceIds, setUnresolvedSourceIds] = useState<string[]>([]);
    // 실패 재생 뒤 「수강권 목록 확인」이 필요한 원본(24시간 기억, pendingStore). 사용자가 확인을 체크해야 새 키로 다시 보낸다.
    const [reviewKinds, setReviewKinds] = useState<Record<string, TransferReviewKind>>({});
    const [isReviewConfirmed, setIsReviewConfirmed] = useState(false);
    // 응답 유실·5xx 로 처리 여부를 모르는 요청. 자동으로 다시 보내지 않고, 사용자가 「같은 요청 다시 확인」을
    // 누를 때만 이 본문(같은 키)을 1회 다시 보낸다. 있는 동안 버튼은 입력과 무관하게 이 요청만 보낸다.
    const [manualRetry, setManualRetry] = useState<TransferAttempt | null>(null);
    // 창을 다시 열었는데 10분 안에 결과를 모르는 요청이 있던 원본을 고른 경우(sessionStorage 에서 복원).
    const [isRestoredRetry, setIsRestoredRetry] = useState(false);
    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // 원본을 바꾸면 확인 체크를 풀고, 저장된 「수강권 목록 확인 필요」 기록을 불러온다(창을 다시 열어도 유지).
    useEffect(() => {
        setIsReviewConfirmed(false);
        if (!selectedSourceId) return;
        const stored = loadTransferReview(selectedSourceId);
        if (stored) setReviewKinds((prev) => ({ ...prev, [selectedSourceId]: stored }));
    }, [selectedSourceId]);

    // 결과를 모르는 요청이 남아 있는 원본을 고르면 같은 키·같은 본문으로만 다시 확인하게 한다.
    useEffect(() => {
        if (!selectedSourceId || manualRetry || unresolvedSourceIds.includes(selectedSourceId)) return;
        // 보내는 중에는 방금 저장한 자기 요청을 다시 불러오지 않는다.
        if (isSubmittingRef.current) return;
        const stored = loadPendingTransfer<TransferAttempt>(selectedSourceId);
        if (!stored) {
            // 10분이 지나 같은 키 기억이 만료됐으면 저장소가 확인 요구로 바꿔 둔다 — 화면에도 반영한다.
            const review = loadTransferReview(selectedSourceId);
            if (review) setReviewKinds((prev) => (prev[selectedSourceId] === review ? prev : { ...prev, [selectedSourceId]: review }));
            return;
        }
        const { savedAt: _savedAt, ...restored } = stored;
        setManualRetry(restored);
        setIsRestoredRetry(true);
    }, [selectedSourceId, manualRetry, unresolvedSourceIds]);

    useEffect(() => {
        const handle = setTimeout(() => {
            setDebouncedSearch(recipientSearch.trim());
        }, 250);
        return () => clearTimeout(handle);
    }, [recipientSearch]);

    const { data: membersData, isLoading: isMembersLoading, isError: isMembersError } = usePublicMemberList({
        centerId,
        search: debouncedSearch || undefined,
        status: 'active',
        limit: 30,
        enabled: !!centerId,
    });
    const {
        data: options = [],
        isLoading: isOptionsLoading,
        isError: isOptionsError,
        error: optionsError,
    } = useTicketTransferOptions({ centerId, memberId });
    const transferMutation = useTransferTicket();
    // 받는 회원의 현재 담당 강사 — "그 강사에게 바로 배정" 바로가기 칩에 쓴다.
    const {
        data: recipientCoaches,
        isLoading: isRecipientCoachesLoading,
        isError: isRecipientCoachesError,
    } = useMemberCoaches({
        memberId: isSplit ? memberId : selectedRecipientId,
        centerId,
        enabled: isSplit ? !!memberId : !!selectedRecipientId,
    });

    const members = useMemo(() => membersData?.results ?? [], [membersData]);
    const filteredMembers = useMemo(() => {
        return members
            .filter((member: ICenterMember) => resolveMemberId(member) !== memberId)
            .slice(0, 30);
    }, [members, memberId]);

    const selectedOption = options.find((option) => option.sourceId === selectedSourceId);
    // 같은 회원 나눔이면 받는 회원은 본인이다.
    const effectiveRecipientId = isSplit ? memberId : selectedRecipientId;
    const selectedRecipientName = isSplit ? memberName || '' : selectedRecipientSnapshot?.name || '';
    // 담당 강사 선택은 PT 수강권에만 있다. 수강권을 바꾸면 원본 강사도 바뀌므로 선택을 되돌린다.
    const isCoachSelectable = isCoachSelectableOption(selectedOption);
    const sourceCoachId = selectedOption?.coachId || '';
    const sourceCoachName = selectedOption?.coachName || '';
    const coachChoices = useMemo(
        () => (coachOptions ?? []).filter((coach) => coach.code && coach.code !== sourceCoachId),
        [coachOptions, sourceCoachId],
    );
    const coachSelectionKey = makeCoachSelectionKey(selectedSourceId, effectiveRecipientId);
    const storedSelectionForKey = readCoachSelectionForKey(storedCoachSelection, coachSelectionKey);
    // 나눔은 강사를 반드시 바꾼다. '유지' 선택지가 없고, 강사를 고르기 전까지는 미완료다.
    const coachSelection: TransferCoachSelection = isSplit
        ? { mode: 'CHANGE', coachId: storedSelectionForKey.coachId }
        : storedSelectionForKey;
    const setCoachSelection = (selection: TransferCoachSelection) =>
        setStoredCoachSelection({ key: coachSelectionKey, selection });
    const coachChoiceIds = useMemo(() => coachChoices.map((coach) => coach.code), [coachChoices]);
    const selectedCoachOption = coachChoices.find((coach) => coach.code === coachSelection.coachId);
    // 화면 목록에 없는 강사 ID 는 변경으로 치지 않는다(고른 뒤 목록에서 빠진 경우 제출 차단).
    const changedCoachId = resolveChangedCoachId(selectedOption, coachSelection, coachChoiceIds);
    const changedCoachName = changedCoachId
        ? coachChoices.find((coach) => coach.code === changedCoachId)?.value || ''
        : '';
    const recipientCoachSuggestions = useMemo(
        () =>
            resolveRecipientCoachSuggestions({
                recipientCoaches,
                coachOptions: coachChoices,
                sourceCoachId,
            }),
        [recipientCoaches, coachChoices, sourceCoachId],
    );
    const isRecipientAlreadyWithSourceCoach = !!(
        sourceCoachId && (recipientCoaches ?? []).some((relation) => relation.coachId === sourceCoachId)
    );
    const clearSelectedRecipient = () => {
        setSelectedRecipientId('');
        setSelectedRecipientSnapshot(null);
    };
    const countNumber = Number(transferCount);
    // 상한은 서버가 정한다. 미래 일정이 있어도 그 몫을 뺀 나머지는 양도할 수 있고,
    // maxTransferableCount 를 안 내려주는 구버전 서버에서는 잔여 회차로 폴백한다.
    const selectedMaxTransferable = resolveMaxTransferableCount(selectedOption);
    // 상한이 잔여보다 작아진 이유(그룹 미차감 점유). 없으면 null 이라 아무것도 뜨지 않는다.
    const selectedLimitReserved = getTransferLimitNotice(selectedOption)
        ? resolveUnconsumedFutureCount(selectedOption)
        : 0;
    const isValidCount = !!selectedOption && isTransferCountWithinLimit(selectedOption, countNumber);
    const isCoachIncomplete = isCoachSelectionIncomplete(selectedOption, coachSelection, coachChoiceIds);
    const isRetryingSameKey = sameKeyRetryCount > 0;
    const isSelectedSourceUnresolved = !!selectedSourceId && unresolvedSourceIds.includes(selectedSourceId);
    const selectedReviewKind: TransferReviewKind | null = selectedSourceId ? reviewKinds[selectedSourceId] ?? null : null;
    const isReviewPending = !!selectedReviewKind && !isReviewConfirmed;
    // 상품 분류 적용 겹침으로 보류된 요청은 같은 키로 다시 보내기 전에도 수강권 목록 확인 체크를 받는다(5회차 지적 4).
    const isBusyRetryReviewPending = manualRetry?.origin === 'busy' && !isReviewConfirmed;
    const canSubmit =
        !transferMutation.isLoading &&
        !isRetryingSameKey &&
        ((!!manualRetry && !isBusyRetryReviewPending) ||
        (!isSelectedSourceUnresolved &&
        !isReviewPending &&
        !!selectedOption &&
        !!effectiveRecipientId &&
        isValidCount &&
        !isCoachIncomplete &&
        (!isSplit || isSplitSelectableOption(selectedOption))));
    const splitActionLabel =
        isSplit && changedCoachId && isValidCount
            ? getSplitActionLabel({ fromCoachName: sourceCoachName, toCoachName: changedCoachName, count: countNumber })
            : '';
    const handleChangeRecipientMode = (mode: TransferRecipientMode) => {
        if (mode === recipientMode) return;
        setRecipientMode(mode);
        // 회차 나누기로 바꾸면 양도에서 미리 채운 회차를 비운다(나눌 회차는 직접 정한다).
        if (mode === 'SAME') setTransferCount('');
        // 그룹 수강권은 나눌 수 없다. 골라 둔 것이 그룹이면 선택을 비운다.
        if (mode === 'SAME' && selectedOption && !isSplitSelectableOption(selectedOption)) {
            setSelectedSourceId('');
            setTransferCount('');
        }
    };

    const handleTransfer = async () => {
        if (manualRetry) {
            // 이 창이 쥔 요청이 그 사이 닫힌 창의 늦은 응답으로 「겹침 보류」가 됐을 수 있다 — 보내기 직전에 저장소를 다시 읽는다(7회차 3).
            const stored = loadPendingTransfer<TransferAttempt>(manualRetry.body.sourceId);
            if (
                manualRetry.origin !== 'busy' &&
                stored?.origin === 'busy' &&
                stored.body.clientOperationId === manualRetry.body.clientOperationId
            ) {
                setManualRetry({ ...manualRetry, origin: 'busy' });
                setIsReviewConfirmed(false);
                createAlert(isSplit ? '회차 나누기 확인' : '수강권 양도 확인', TRANSFER_REVIEW_REQUIRED_MESSAGE, true);
                return;
            }
            if (manualRetry.origin === 'busy') {
                if (!isReviewConfirmed) {
                    createAlert(isSplit ? '회차 나누기 확인' : '수강권 양도 확인', TRANSFER_REVIEW_REQUIRED_MESSAGE, true);
                    return;
                }
                // 확인은 이번 한 번의 재전송에만 쓴다(또 겹치면 다시 확인받는다).
                setIsReviewConfirmed(false);
            }
            await sendTransfer(manualRetry, true);
            return;
        }
        if (!selectedOption || !effectiveRecipientId) return;
        if (unresolvedSourceIds.includes(selectedOption.sourceId)) {
            createAlert(isSplit ? '회차 나누기 확인' : '수강권 양도 확인', TICKET_TRANSFER_UNRESOLVED_BLOCK_MESSAGE, true);
            return;
        }
        if (isReviewPending) {
            createAlert(isSplit ? '회차 나누기 확인' : '수강권 양도 확인', TRANSFER_REVIEW_REQUIRED_MESSAGE, true);
            return;
        }
        if (isSplit && !isSplitSelectableOption(selectedOption)) {
            createAlert('회차 나누기 확인', '그룹 수강권은 회차 나누기를 할 수 없어요.', true);
            return;
        }
        if (!isValidCount) {
            createAlert(
                isSplit ? '나눌 회차 확인' : '양도 회차 확인',
                `1회 이상 ${selectedMaxTransferable}회 이하로 입력해주세요.`,
                true
            );
            return;
        }
        if (isCoachIncomplete) {
            createAlert(
                '담당 강사 확인',
                isSplit
                    ? '나눠 받을 강사를 골라 주세요.'
                    : '양도 수강권을 받을 강사를 선택하거나, 현재 담당 강사 유지를 선택해주세요.',
                true
            );
            return;
        }
        // toCoachId 는 원본 강사와 다를 때만 실린다(구서버 호환 — coachSelection.ts 참조).
        // 제출마다 새 멱등 키(PT 만, 그룹은 싣지 않는다). 재확인은 이 본문 그대로 보낸다.
        const bodyWithKey = attachClientOperationId(
            buildTransferTicketBody({
                option: selectedOption,
                toMemberId: effectiveRecipientId,
                transferCount: countNumber,
                policy,
                memo,
                selection: coachSelection,
                availableCoachIds: coachChoiceIds,
            }),
            null,
        );
        // 이 세션에서 서버가 키를 거부한 적이 있으면(구서버) 처음부터 키 없이 보낸다.
        const body = isClientOperationIdUnsupported() ? stripClientOperationId(bodyWithKey) : bodyWithKey;
        // 수강권 목록을 확인했다고 체크하고 보내는 것이다 — 확인 필요 기록을 지운다(이번 결과에 따라 다시 생길 수 있다).
        if (selectedReviewKind) {
            clearTransferReview(selectedOption.sourceId);
            setReviewKinds(({ [selectedOption.sourceId]: _done, ...rest }) => rest);
            setIsReviewConfirmed(false);
        }
        await sendTransfer(
            {
                body,
                isSplit,
                ticketName: selectedOption.name,
                recipientName: selectedRecipientName,
                fromCoachName: sourceCoachName,
                toCoachName: changedCoachName,
            },
            false,
        );
    };

    /**
     * 결과(성공·깨끗한 실패)로 확인 요구를 푼다 — 그 확인 요구를 만든 요청 키와 응답의 키가 같을 때만(6회차 지적 1).
     * 닫힌 창에 늦게 도착한 응답도 여기까지는 처리하지만, 다른 요청이 만든 확인 요구는 건드리지 않는다.
     */
    const releaseTransferReview = (sourceId: string, operationKey: string | undefined) => {
        if (!clearTransferReviewForOperation(sourceId, operationKey)) return;
        if (!isMountedRef.current) return;
        setReviewKinds((prev) => {
            if (!(sourceId in prev)) return prev;
            const { [sourceId]: _done, ...rest } = prev;
            return rest;
        });
    };

    const sendTransfer = async (attempt: TransferAttempt, isManualRetry: boolean) => {
        // 빠른 더블클릭 등 동기 재진입 방지 (isLoading 리렌더 전 2차 클릭 차단)
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        let { body } = attempt;
        let currentAttempt = attempt;
        let keyStripped = false;
        const failTitle = attempt.isSplit ? '회차 나누기 실패' : '수강권 양도 실패';
        let autoRetriesUsed = 0;
        // 원본은 재전송 중에도 바뀌지 않는다(키만 빠질 수 있다). 루프 안 클로저가 바뀌는 body 를 잡지 않게 따로 둔다.
        const sourceIdForBlock = attempt.body.sourceId;

        // 키를 실은 요청은 보내기 **직전에** 원본 기준으로 기억한다. 요청 중·자동 재확인 대기 중에 창을 닫아도
        // 다시 열면 같은 키·같은 본문으로만 확인하게 된다. 성공 또는 서버가 쓰기 전에 거부한 확정 실패에서만 지운다.
        savePendingTransfer(attempt);

        try {
            for (;;) {
                try {
                    const result = await transferMutation.mutateAsync({
                        centerId,
                        fromMemberId: memberId,
                        body,
                    });
                    clearPendingTransferForOperation(body.sourceId, body.clientOperationId);
                    // 결과를 받았다 — 이 원본의 확인 요구도 푼다(같은 키 재확인 성공 포함).
                    releaseTransferReview(body.sourceId, body.clientOperationId);
                    if (!isMountedRef.current) return;
                    setManualRetry(null);
                    setIsRestoredRetry(false);
                    const replayNotice = result?.data?.idempotentReplay ? ' 앞서 보낸 요청이 이미 처리돼 있었어요.' : '';
                    if (attempt.isSplit) {
                        createAlert(
                            '회차 나누기 완료',
                            `${getSplitActionLabel({
                                fromCoachName: attempt.fromCoachName,
                                toCoachName: attempt.toCoachName,
                                count: body.transferCount,
                            }).replace(/ 나누기$/, '')} 나눴어요.${replayNotice}`,
                            false
                        );
                    } else {
                        const coachNotice = body.toCoachId
                            ? ` 담당 강사는 ${attempt.toCoachName || '선택한'} 강사로 배정되었습니다.`
                            : '';
                        createAlert(
                            '수강권 양도 완료',
                            `${memberName || '회원'}님의 ${attempt.ticketName} ${body.transferCount}회가 ${attempt.recipientName || '선택 회원'}님에게 양도되었습니다.${coachNotice}${replayNotice}`,
                            false
                        );
                    }
                    onSuccess?.();
                    setIsVisible(false);
                    return;
                } catch (error: any) {
                    if (!isMountedRef.current) {
                        // 창이 닫힌 뒤 도착한 응답. 화면 상태는 건드리지 않되, 첫 응답이 일괄 적용 겹침(409·503)이면
                        // 보류 기록(같은 키·같은 본문 + 확인 필요 표시)을 남겨, 다시 열었을 때 체크 요구가 걸리게 한다(7회차).
                        // 같은 요청의 기억일 때만 바꾼다 — 다른 요청의 기억은 그대로.
                        if (
                            body.clientOperationId &&
                            classifySameKeyFailure(error, {
                                autoRetriesUsed,
                                isManualRetry: isManualRetry && attempt.origin !== 'busy',
                            }) === 'BUSY_KEEP_KEY'
                        ) {
                            updatePendingTransferForOperation({ ...currentAttempt, origin: 'busy' as const });
                        }
                        return;
                    }
                    if (body.clientOperationId && !keyStripped && isClientOperationIdRejected(error)) {
                        markClientOperationIdUnsupported();
                        if (!isManualRetry && autoRetriesUsed === 0) {
                            // 구서버가 검증 단계에서 키를 모르는 필드로 거부했다 — 아무것도 쓰지 않았으므로
                            // 키만 빼고 **1회** 다시 보낸다. 이후 이 세션은 키 없이 보낸다.
                            keyStripped = true;
                            // 키를 실은 요청은 검증 단계에서 거부돼 아무것도 쓰지 않았다 — 그 요청의 기억을 지운다.
                            clearPendingTransferForOperation(sourceIdForBlock, body.clientOperationId);
                            body = stripClientOperationId(body);
                            currentAttempt = { ...attempt, body };
                            continue;
                        }
                        // 응답 유실 뒤 재확인 중에 서버가 구버전으로 바뀌었다. 앞 요청이 새 서버에서 처리됐을 수 있어
                        // 키 없이 다시 보내면 두 번째 양도가 된다 → 결과 미확인으로 멈춘다.
                        setManualRetry(null);
                        setIsRestoredRetry(false);
                        setUnresolvedSourceIds((prev) =>
                            prev.includes(sourceIdForBlock) ? prev : [...prev, sourceIdForBlock],
                        );
                        createAlert(failTitle, TICKET_TRANSFER_RETRY_EXHAUSTED_MESSAGE, true);
                        return;
                    }
                    const outcome = body.clientOperationId
                        ? classifySameKeyFailure(error, {
                              autoRetriesUsed,
                              // 일괄 적용 겹침으로 쥐고 있던 요청의 재전송은 같은 키의 새 제출과 같다(재확인이 아니다).
                              isManualRetry: isManualRetry && attempt.origin !== 'busy',
                          })
                        : 'NEW_KEY';
                    if (outcome === 'RETRY_SAME_KEY') {
                        // 서버가 「처리 중」이라고 답했다(인덱스가 살아 있다는 뜻) — 같은 키 재전송이 안전하다.
                        const delay = getSameKeyRetryDelayMs(autoRetriesUsed) ?? 0;
                        autoRetriesUsed += 1;
                        setSameKeyRetryCount(autoRetriesUsed);
                        await new Promise((resolve) => setTimeout(resolve, delay));
                        if (!isMountedRef.current) return;
                        continue;
                    }
                    if (outcome === 'ASK_MANUAL_RETRY') {
                        // 응답 유실·5xx. 자동으로 다시 보내지 않는다(인덱스 미준비 기간엔 두 번째 양도가 된다).
                        // 수강권·잔여는 mutation onError 가 다시 불러온다.
                        setManualRetry(asUnconfirmedAttempt(currentAttempt));
                        savePendingTransfer(asUnconfirmedAttempt(currentAttempt));
                        createAlert(failTitle, TICKET_TRANSFER_UNCONFIRMED_MESSAGE, true);
                        return;
                    }
                    if (outcome === 'BUSY_KEEP_KEY') {
                        // 상품 분류 일괄 적용과 겹쳤다. 409 는 드물게 쓰기 뒤에도 오므로(서버가 되돌린 뒤 FAILED) 키를 버리지 않는다.
                        // 응답 유실 흐름과 같이 같은 키·같은 본문을 저장소에 남겨, 창을 다시 열어도 그 요청으로만 다시 보내게 한다.
                        // 쓰기 전 거절이었다면 서버에 기록이 없어 같은 키 재전송이 정상 처리된다.
                        if (body.clientOperationId) {
                            const busyAttempt: TransferAttempt = { ...currentAttempt, origin: 'busy' };
                            setIsReviewConfirmed(false);
                            setManualRetry(busyAttempt);
                            savePendingTransfer(busyAttempt);
                        } else {
                            // 키 없는 요청은 대기 기억을 만들지 않는다 — 다른 요청의 기억을 건드리지 않는다.
                            setManualRetry(null);
                        }
                        setIsRestoredRetry(false);
                        createAlert(
                            failTitle,
                            `${getTicketTransferErrorMessage(error) ?? '잠시 후 다시 시도해 주세요.'} 끝나면 회원의 수강권 목록을 확인한 뒤 「같은 요청 다시 확인」으로 같은 요청을 다시 보내 주세요.`,
                            true
                        );
                        return;
                    }
                    if (outcome === 'ROLLBACK_INCOMPLETE') {
                        // 앞 요청이 실패했고 서버가 깨끗하다고 증명하지 못했다(rollbackComplete false). 새 수강권이 남았을 수 있어
                        // 사용자가 수강권 목록을 확인했다고 체크하기 전에는 새 키로 다시 보내지 못한다(24시간 기억).
                        clearPendingTransferForOperation(sourceIdForBlock, body.clientOperationId);
                        setManualRetry(null);
                        setIsRestoredRetry(false);
                        saveTransferReview(sourceIdForBlock, 'incomplete', Date.now(), body.clientOperationId);
                        setReviewKinds((prev) => ({ ...prev, [sourceIdForBlock]: 'incomplete' }));
                        setIsReviewConfirmed(false);
                        createAlert(failTitle, TICKET_TRANSFER_ROLLBACK_INCOMPLETE_MESSAGE, true);
                        return;
                    }
                    if (outcome === 'UNRESOLVED') {
                        // 처리됐는지 모른다. 새 키로 다시 보내지 않고, 이 창에서 같은 수강권 제출을 막는다.
                        // 창을 다시 열면 10분 동안은 같은 키·같은 본문으로만 다시 확인할 수 있다.
                        savePendingTransfer(asUnconfirmedAttempt(currentAttempt));
                        setManualRetry(null);
                        setIsRestoredRetry(false);
                        setUnresolvedSourceIds((prev) =>
                            prev.includes(sourceIdForBlock) ? prev : [...prev, sourceIdForBlock],
                        );
                        createAlert(
                            failTitle,
                            isTicketTransferStaleFailure(error)
                                ? TICKET_TRANSFER_STALE_MESSAGE
                                : TICKET_TRANSFER_RETRY_EXHAUSTED_MESSAGE,
                            true
                        );
                        return;
                    }
                    // 서버가 확정 실패를 알려 줬다. 다음 제출은 새 키다.
                    clearPendingTransferForOperation(body.sourceId, body.clientOperationId);
                    setManualRetry(null);
                    setIsRestoredRetry(false);
                    // 실패 재생이 깨끗한 실패를 증명했다(rollbackComplete true) — 이 원본의 확인 요구를 푼다.
                    if (getTicketTransferRollbackState(error) === 'complete') {
                        releaseTransferReview(sourceIdForBlock, body.clientOperationId);
                    }
                    // 실패 재생인데 되돌리기 결과를 모르면(지금 배포 중인 서버·옛 기록) 새 키 전에 수강권 목록 확인을 받는다.
                    if (getTicketTransferRollbackState(error) === 'unknown') {
                        saveTransferReview(sourceIdForBlock, 'unknown', Date.now(), body.clientOperationId);
                        setReviewKinds((prev) => ({ ...prev, [sourceIdForBlock]: 'unknown' }));
                        setIsReviewConfirmed(false);
                    }
                    // 상한 초과(409)는 서버가 실어 보낸 최신 상한으로 입력값을 바로 보정한다.
                    const maxFromError = getTicketTransferMaxFromError(error);
                    if (maxFromError !== null && maxFromError > 0) {
                        setTransferCount(String(maxFromError));
                    }
                    createAlert(
                        failTitle,
                        getTicketTransferErrorMessage(error) ||
                            error?.serverMessage ||
                            error?.message ||
                            '수강권 양도에 실패했습니다.',
                        true
                    );
                    return;
                }
            }
        } finally {
            isSubmittingRef.current = false;
            if (isMountedRef.current) setSameKeyRetryCount(0);
        }
    };

    const renderOption = (option: TicketTransferOption) => {
        const isSelected = selectedSourceId === option.sourceId;
        const maxTransferable = resolveMaxTransferableCount(option);
        // 차단이 아니라 설명이다. 미래 일정이 있어도 상한만큼은 그냥 양도된다.
        const limitNotice = getTransferLimitNotice(option);
        const unavailableNotice = getTransferUnavailableNotice(option);
        // 회차 나누기는 PT 수강권만 된다. 그룹은 나눔 모드에서 고를 수 없다.
        const isBlockedForSplit = isSplit && !isSplitSelectableOption(option);
        const isSelectable = option.canTransfer && !isBlockedForSplit;

        return (
            <button
                key={`${option.sourceType}-${option.sourceId}`}
                disabled={!isSelectable}
                onClick={() => {
                    setSelectedSourceId(option.sourceId);
                    // 양도는 최대 회차를 미리 채운다. 회차 나누기는 몇 회를 나눌지 직접 정하도록 비워 둔다.
                    setTransferCount(!isSplit && maxTransferable > 0 ? String(maxTransferable) : '');
                }}
                style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: 10,
                    border: isSelected ? '1.5px solid #3B82F6' : '1px solid #E5E7EB',
                    backgroundColor: isSelected ? '#EFF6FF' : isSelectable ? '#FFFFFF' : '#F9FAFB',
                    cursor: isSelectable ? 'pointer' : 'not-allowed',
                    textAlign: 'left',
                    opacity: isSelectable ? 1 : 0.56,
                }}
            >
                <Row style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <Col style={{ gap: 6, minWidth: 0 }}>
                        <Row style={{ alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{option.name}</span>
                            <span
                                style={{
                                    ...pillStyle,
                                    backgroundColor: option.sourceType === 'MEMBERSHIP' ? '#DBEAFE' : '#D1FAE5',
                                    color: option.sourceType === 'MEMBERSHIP' ? '#1D4ED8' : '#047857',
                                }}
                            >
                                {option.sourceLabel}
                            </span>
                            {option.coachName && (
                                <span style={{ fontSize: 13, color: '#6B7280' }}>{option.coachName}</span>
                            )}
                        </Row>
                        <span style={{ fontSize: 13, color: '#6B7280' }}>
                            잔여 {option.remainingCount}회 · 현재 {option.currentSession}회차 · 만료 {option.expirationDate ? dayjs(option.expirationDate).format('YYYY.MM.DD') : '-'}
                        </span>
                        {limitNotice && (
                            <span style={{ fontSize: 13, color: '#6B7280' }}>{limitNotice}</span>
                        )}
                        {unavailableNotice && (
                            <span style={{ fontSize: 13, color: '#6B7280' }}>{unavailableNotice}</span>
                        )}
                        {isBlockedForSplit && option.canTransfer && (
                            <span style={{ fontSize: 13, color: '#6B7280' }}>그룹 수강권은 회차 나누기를 할 수 없어요</span>
                        )}
                        {option.isExpired && (
                            <span style={{ fontSize: 13, color: '#DC2626' }}>
                                만료된 수강권은 양도할 수 없습니다
                            </span>
                        )}
                    </Col>
                    <span style={{ fontSize: 14, fontWeight: 700, color: isSelected ? '#2563EB' : '#9CA3AF' }}>
                        {option.remainingCount}회
                    </span>
                </Row>
            </button>
        );
    };

    return (
        <Modal
            isCloseButton
            setIsVisible={setIsVisible}
            whiteViewWidth={980}
            whiteViewHeight={790}
            isCloseOutside={false}
        >
            <Col style={{ padding: '32px 36px', gap: 22 }}>
                <Col style={{ gap: 6 }}>
                    <Subtitle1Bold style={{ color: colors.LAVEL_4 }}>{isSplit ? '회차 나누기' : '수강권 양도'}</Subtitle1Bold>
                    <Body3Regular style={{ color: colors.LAVEL_2 }}>
                        {isSplit
                            ? `${memberName || '회원'}님의 남은 회차를 다른 강사에게 나눠요.`
                            : `${memberName || '회원'}님의 잔여 수강권을 다른 회원에게 독립 수강권으로 생성합니다.`}
                    </Body3Regular>
                </Col>

                {manualRetry && (
                    <Col
                        style={{
                            gap: 6,
                            padding: '12px 14px',
                            borderRadius: 10,
                            backgroundColor: '#FFFBEB',
                            border: '1px solid #FDE68A',
                        }}
                    >
                        <Body3Regular style={{ color: '#92400E', fontWeight: 700 }}>
                            {manualRetry.origin === 'busy'
                                ? '상품 분류를 적용하는 중이라 처리하지 못한 요청이 있어 입력을 잠갔어요. 같은 요청으로만 다시 보낼 수 있어요.'
                                : isRestoredRetry
                                ? '이 수강권으로 보낸 요청의 결과를 아직 확인하지 못했어요. 같은 요청으로만 다시 확인할 수 있어요.'
                                : '처리 결과를 확인하지 못한 요청이 있어 입력을 잠갔어요. 같은 요청으로만 다시 확인할 수 있어요.'}
                        </Body3Regular>
                        <Body3Regular style={{ color: '#92400E', lineHeight: 1.6 }}>
                            {`다시 확인할 요청: ${manualRetry.ticketName} ${manualRetry.body.transferCount}회 → ${
                                manualRetry.isSplit
                                    ? `${manualRetry.toCoachName || '고른'} 강사에게 나누기`
                                    : `${manualRetry.recipientName || '선택한 회원'}님${
                                          manualRetry.body.toCoachId ? ` (담당 ${manualRetry.toCoachName || '선택한'} 강사)` : ''
                                      }`
                            }`}
                        </Body3Regular>
                        {!isRetryingSameKey && !transferMutation.isLoading && (
                            <button
                                type="button"
                                onClick={() => {
                                    // 대기를 내려놓고 다른 수강권을 고른다. 같은 원본을 다시 고르면 같은 요청이 다시 걸린다.
                                    setManualRetry(null);
                                    setIsRestoredRetry(false);
                                    setSelectedSourceId('');
                                    setTransferCount('');
                                }}
                                style={{
                                    alignSelf: 'flex-start',
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    color: '#2563EB',
                                    fontSize: 13,
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                }}
                            >
                                다른 수강권 고르기
                            </button>
                        )}
                    </Col>
                )}

                {/* 잠금은 마우스뿐 아니라 키보드 진입도 막는다(fieldset disabled 가 안의 입력·버튼을 비활성화). */}
                <fieldset
                    disabled={!!manualRetry || isRetryingSameKey || transferMutation.isLoading}
                    style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
                >
                <Row
                    style={{
                        gap: 24,
                        alignItems: 'flex-start',
                        // 같은 요청 재확인 대기·전송 중에는 입력을 잠근다(화면 요약과 실제 본문이 어긋나지 않게).
                        ...(manualRetry || isRetryingSameKey || transferMutation.isLoading
                            ? { pointerEvents: 'none' as const, opacity: 0.55 }
                            : {}),
                    }}
                    aria-disabled={!!manualRetry || isRetryingSameKey || transferMutation.isLoading}
                >
                    <Col style={{ flex: 1, gap: 20, minWidth: 0 }}>
                        <Col>
                            {/*
                              받는 쪽 선택은 제목 줄 오른쪽 작은 세그먼트로 둔다 — 기존 화면 높이·순서를 그대로 지키고,
                              자주 쓰지 않는 갈래(같은 회원 · 다른 강사와 회차 나누기)에 시선이 먼저 가지 않게.
                            */}
                            <Row
                                style={{
                                    ...sectionTitleStyle,
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <span>{isSplit ? '1. 나눌 대상' : '1. 양도받을 회원'}</span>
                                <Row
                                    role="radiogroup"
                                    aria-label="받는 쪽"
                                    style={{ gap: 2, padding: 2, borderRadius: 7, backgroundColor: '#F3F4F6', height: 22 }}
                                >
                                    {([
                                        { mode: 'OTHER' as const, label: '다른 회원에게' },
                                        { mode: 'SAME' as const, label: '같은 회원 · 다른 강사와 회차 나누기' },
                                    ]).map((item) => {
                                        const selected = recipientMode === item.mode;
                                        return (
                                            <button
                                                key={item.mode}
                                                type="button"
                                                role="radio"
                                                aria-checked={selected}
                                                onClick={() => handleChangeRecipientMode(item.mode)}
                                                style={{
                                                    height: 18,
                                                    padding: '0 8px',
                                                    borderRadius: 5,
                                                    border: 'none',
                                                    backgroundColor: selected ? '#FFFFFF' : 'transparent',
                                                    boxShadow: selected ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                                                    color: selected ? '#111827' : '#6B7280',
                                                    fontSize: 12,
                                                    fontWeight: selected ? 700 : 500,
                                                    lineHeight: '18px',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {item.label}
                                            </button>
                                        );
                                    })}
                                </Row>
                            </Row>
                            {isSplit ? (
                                <Col
                                    style={{
                                        gap: 4,
                                        padding: 14,
                                        borderRadius: 12,
                                        border: '1.5px solid #3B82F6',
                                        backgroundColor: '#EFF6FF',
                                    }}
                                >
                                    <span style={{ fontSize: 16, fontWeight: 700, color: '#0F172A' }}>
                                        {memberName || '회원'}님 그대로
                                    </span>
                                    <span style={{ fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
                                        강사만 바꿔서 남은 회차를 나눠요. PT 수강권만 나눌 수 있어요.
                                    </span>
                                </Col>
                            ) : selectedRecipientId && selectedRecipientSnapshot ? (
                                <Col
                                    style={{
                                        gap: 8,
                                        padding: 14,
                                        marginBottom: 10,
                                        borderRadius: 12,
                                        border: '1.5px solid #3B82F6',
                                        backgroundColor: '#EFF6FF',
                                    }}
                                >
                                    <Row style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                                        <Row style={{ alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                                            <span
                                                style={{
                                                    padding: '2px 8px',
                                                    borderRadius: 999,
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    backgroundColor: '#2563EB',
                                                    color: '#FFFFFF',
                                                }}
                                            >
                                                선택됨
                                            </span>
                                            <span style={{ fontSize: 16, fontWeight: 700, color: '#0F172A' }}>
                                                {selectedRecipientSnapshot.name}
                                            </span>
                                            {selectedRecipientSnapshot.memberType && (
                                                <span
                                                    style={{
                                                        padding: '2px 6px',
                                                        borderRadius: 6,
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                        backgroundColor:
                                                            selectedRecipientSnapshot.memberType === 'public' ? '#FEF3C7' : '#DBEAFE',
                                                        color:
                                                            selectedRecipientSnapshot.memberType === 'public' ? '#B45309' : '#1D4ED8',
                                                    }}
                                                >
                                                    {resolveMemberTypeLabel(selectedRecipientSnapshot.memberType)}
                                                </span>
                                            )}
                                            <AppAccountStatusBadge member={selectedRecipientSnapshot} />
                                        </Row>
                                        <button
                                            onClick={clearSelectedRecipient}
                                            style={{
                                                padding: '6px 12px',
                                                height: 32,
                                                borderRadius: 8,
                                                border: '1px solid #BFDBFE',
                                                backgroundColor: '#FFFFFF',
                                                color: '#2563EB',
                                                fontSize: 13,
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                            }}
                                        >
                                            변경
                                        </button>
                                    </Row>
                                    <span style={{ fontSize: 14, color: '#475569' }}>
                                        {formatKoreanPhoneNumber(selectedRecipientSnapshot.phoneNumber, '-')}
                                    </span>
                                </Col>
                            ) : (
                            <>
                            <Row style={{ gap: 8, marginBottom: 10 }}>
                                <input
                                    style={textInputStyle}
                                    placeholder="이름 또는 휴대폰 번호 검색"
                                    value={recipientSearch}
                                    onChange={(e) => setRecipientSearch(e.target.value)}
                                />
                            </Row>
                            <Col style={{ maxHeight: 190, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 10 }}>
                                {isMembersLoading ? (
                                    <Body3Regular style={{ color: '#9CA3AF', padding: 16 }}>회원을 검색하는 중입니다.</Body3Regular>
                                ) : isMembersError ? (
                                    <Body3Regular style={{ color: '#DC2626', padding: 16 }}>회원을 불러오지 못했습니다.</Body3Regular>
                                ) : filteredMembers.length === 0 ? (
                                    <Body3Regular style={{ color: '#9CA3AF', padding: 16 }}>검색 결과가 없습니다.</Body3Regular>
                                ) : (
                                    filteredMembers.map((member: ICenterMember) => {
                                        const id = resolveMemberId(member);
                                        const selected = selectedRecipientId === id;
                                        return (
                                            <button
                                                key={id}
                                                onClick={() => {
                                                    setSelectedRecipientId(id);
                                                    setSelectedRecipientSnapshot({
                                                        name: member.name,
                                                        phoneNumber: member.phoneNumber,
                                                        memberType: member.memberType,
                                                        appLinked: member.appLinked,
                                                        isInfo: member.isInfo,
                                                        appAccountStatus: member.appAccountStatus,
                                                        appWithdrawnAt: member.appWithdrawnAt,
                                                    });
                                                    setRecipientSearch('');
                                                }}
                                                style={{
                                                    padding: '10px 12px',
                                                    border: 'none',
                                                    borderBottom: '1px solid #F3F4F6',
                                                    backgroundColor: selected ? '#EFF6FF' : '#FFFFFF',
                                                    textAlign: 'left',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <Row style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                                                    <Col style={{ gap: 2, minWidth: 0 }}>
                                                        <Row style={{ alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                            <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{member.name}</span>
                                                            <span
                                                                style={{
                                                                    padding: '2px 6px',
                                                                    borderRadius: 6,
                                                                    fontSize: 12,
                                                                    fontWeight: 700,
                                                                    backgroundColor: member.memberType === 'public' ? '#FEF3C7' : '#DBEAFE',
                                                                    color: member.memberType === 'public' ? '#B45309' : '#1D4ED8',
                                                                }}
                                                            >
                                                                {resolveMemberTypeLabel(member.memberType)}
                                                            </span>
                                                            <AppAccountStatusBadge member={member} />
                                                        </Row>
                                                        <span style={{ fontSize: 13, color: '#6B7280' }}>{formatKoreanPhoneNumber(member.phoneNumber, '-')}</span>
                                                    </Col>
                                                    {selected && (
                                                        <span style={{ fontSize: 13, fontWeight: 700, color: '#2563EB' }}>선택됨</span>
                                                    )}
                                                </Row>
                                            </button>
                                        );
                                    })
                                )}
                            </Col>
                            </>
                            )}
                        </Col>

                        <Col>
                            <div style={sectionTitleStyle}>{isSplit ? '2. 나눌 수강권' : '2. 양도할 수강권'}</div>
                            <Col style={{ gap: 8, maxHeight: 260, overflowY: 'auto' }}>
                                {isOptionsLoading ? (
                                    <Body3Regular style={{ color: '#9CA3AF' }}>수강권을 불러오는 중입니다.</Body3Regular>
                                ) : isOptionsError ? (
                                    <Body3Regular style={{ color: '#DC2626', lineHeight: 1.5 }}>
                                        수강권을 불러오지 못했습니다. {(optionsError as any)?.serverMessage || (optionsError as any)?.message || ''}
                                    </Body3Regular>
                                ) : options.length === 0 ? (
                                    <Body3Regular style={{ color: '#9CA3AF' }}>양도 가능한 수강권이 없습니다.</Body3Regular>
                                ) : (
                                    options.map(renderOption)
                                )}
                            </Col>
                        </Col>

                        {selectedOption && isCoachSelectable && (
                            <Col
                                style={{
                                    gap: 10,
                                    padding: 14,
                                    borderRadius: 12,
                                    border: '1px solid #E5E7EB',
                                    backgroundColor: '#FFFFFF',
                                }}
                            >
                                <Row style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                                    <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                                        {isSplit ? '나눠 받을 강사' : '양도 수강권의 담당 강사'}
                                    </span>
                                    <span style={{ fontSize: 13, color: '#6B7280' }}>
                                        {isSplit ? '판매담당자는 원본 그대로예요' : '판매담당자는 원본 그대로 유지됩니다'}
                                    </span>
                                </Row>

                                {!isSplit && (
                                <Row style={{ gap: 8 }}>
                                    {([
                                        {
                                            mode: 'KEEP' as const,
                                            title: '현재 담당 강사 유지',
                                            detail: sourceCoachName ? `${sourceCoachName} 강사` : '원본 수강권의 강사',
                                        },
                                        {
                                            mode: 'CHANGE' as const,
                                            title: '다른 강사에게 배정',
                                            detail: '받는 회원의 담당 강사 등',
                                        },
                                    ]).map((item) => {
                                        const selected = coachSelection.mode === item.mode;
                                        const disabled = item.mode === 'CHANGE' && coachChoices.length === 0;
                                        return (
                                            <button
                                                key={item.mode}
                                                type="button"
                                                disabled={disabled}
                                                onClick={() =>
                                                    setCoachSelection(
                                                        item.mode === 'KEEP' ? KEEP_COACH_SELECTION : { mode: 'CHANGE' },
                                                    )
                                                }
                                                style={{
                                                    flex: 1,
                                                    padding: '10px 12px',
                                                    borderRadius: 10,
                                                    border: selected ? '1.5px solid #3B82F6' : '1px solid #E5E7EB',
                                                    backgroundColor: selected ? '#EFF6FF' : '#FFFFFF',
                                                    textAlign: 'left',
                                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                                    opacity: disabled ? 0.56 : 1,
                                                }}
                                            >
                                                <Col style={{ gap: 3 }}>
                                                    <span style={{ fontSize: 14, fontWeight: 700, color: selected ? '#2563EB' : '#111827' }}>
                                                        {item.title}
                                                    </span>
                                                    <span style={{ fontSize: 13, color: '#6B7280' }}>{item.detail}</span>
                                                </Col>
                                            </button>
                                        );
                                    })}
                                </Row>
                                )}

                                {coachChoices.length === 0 && (
                                    <span style={{ fontSize: 13, color: isSplit ? '#DC2626' : '#6B7280' }}>
                                        {isSplit
                                            ? (coachOptions ?? []).length === 0
                                                ? '강사 목록을 불러오지 못했어요. 잠시 뒤 다시 열어 주세요.'
                                                : '이 센터에 나눠 받을 다른 강사가 없어요.'
                                            : (coachOptions ?? []).length === 0
                                            ? '센터 강사 목록을 불러오지 못했거나 아직 로딩 중입니다. 잠시 후 다시 시도하거나, 강사를 유지한 채 양도하세요.'
                                            : '이 센터에 다른 활성 강사가 없어 담당 강사는 유지됩니다.'}
                                    </span>
                                )}

                                {coachSelection.mode === 'CHANGE' && (
                                    <Col style={{ gap: 10 }}>
                                        {recipientCoachSuggestions.length > 0 ? (
                                            <Row style={{ alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                <span style={{ fontSize: 13, color: '#6B7280' }}>
                                                    {isSplit
                                                        ? `${selectedRecipientName || '회원'}님의 다른 담당 강사`
                                                        : `${selectedRecipientName || '받는 회원'}님의 담당 강사`}
                                                </span>
                                                {recipientCoachSuggestions.map((suggestion) => {
                                                    const selected = coachSelection.coachId === suggestion.coachId;
                                                    return (
                                                        <button
                                                            key={suggestion.coachId}
                                                            type="button"
                                                            onClick={() =>
                                                                setCoachSelection({ mode: 'CHANGE', coachId: suggestion.coachId })
                                                            }
                                                            style={{
                                                                ...pillStyle,
                                                                padding: '4px 10px',
                                                                border: selected ? '1.5px solid #3B82F6' : '1px solid #BFDBFE',
                                                                backgroundColor: selected ? '#2563EB' : '#EFF6FF',
                                                                color: selected ? '#FFFFFF' : '#1D4ED8',
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            {suggestion.name}
                                                            {suggestion.isPrimary ? ' · 주담당' : ''}
                                                        </button>
                                                    );
                                                })}
                                            </Row>
                                        ) : (
                                            <span style={{ fontSize: 13, color: isRecipientCoachesError ? '#DC2626' : '#6B7280' }}>
                                                {isSplit
                                                    ? '아래에서 나눠 받을 강사를 골라 주세요.'
                                                    : !selectedRecipientId
                                                    ? '받는 회원을 먼저 선택하면 그 회원의 담당 강사를 바로 고를 수 있습니다.'
                                                    : isRecipientCoachesLoading
                                                    ? `${selectedRecipientName || '받는 회원'}님의 담당 강사를 확인하는 중입니다.`
                                                    : isRecipientCoachesError
                                                    ? `${selectedRecipientName || '받는 회원'}님의 담당 강사를 불러오지 못했습니다. 아래에서 직접 선택하세요.`
                                                    : isRecipientAlreadyWithSourceCoach
                                                    ? `${selectedRecipientName || '받는 회원'}님은 이미 ${sourceCoachName || '현재'} 강사의 회원입니다. 다른 강사가 필요할 때만 선택하세요.`
                                                    : `${selectedRecipientName || '받는 회원'}님에게 배정된 강사가 없습니다. 아래에서 강사를 선택하세요.`}
                                            </span>
                                        )}
                                        {coachSelection.coachId && !selectedCoachOption && (
                                            <span style={{ fontSize: 13, color: '#DC2626' }}>
                                                선택했던 강사가 강사 목록에서 사라졌습니다. 다시 선택해주세요.
                                            </span>
                                        )}
                                        <NewTextInputDropDown
                                            selectData={selectedCoachOption}
                                            datas={coachChoices}
                                            onClickSort={(coach) => setCoachSelection({ mode: 'CHANGE', coachId: coach.code })}
                                            width={420}
                                            fontType={'Body3Regular'}
                                            placeholder="강사 선택"
                                        />
                                        {isSplit && changedCoachId && (
                                            <Col style={{ gap: 2, paddingTop: 2 }}>
                                                <span style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.6 }}>
                                                    • {changedCoachName || '선택한'} 강사 수강권이 새로 생겨요. 0원이라 새 매출은 생기지 않아요.
                                                </span>
                                                <span style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.6 }}>
                                                    • 판매담당자·정산 요율·신규/재등록 구분은 원본 그대로예요.
                                                </span>
                                                <span style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.6 }}>
                                                    • {changedCoachName || '선택한'} 강사와 아직 연결이 없으면 자동으로 연결돼요. {sourceCoachName || '원래'} 강사 연결은 그대로예요.
                                                </span>
                                            </Col>
                                        )}
                                        {!isSplit && changedCoachId && (
                                            <Col style={{ gap: 4, paddingTop: 2 }}>
                                                <Row style={{ alignItems: 'center', gap: 8 }}>
                                                    <SvgIcon name={'SvgError'} size={16} fill={colors.red} />
                                                    <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                                                        강사를 바꿔 양도하면 이렇게 처리됩니다
                                                    </span>
                                                </Row>
                                                <Col style={{ gap: 2, paddingLeft: 24 }}>
                                                    <span style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.6 }}>
                                                        • 양도 수강권은 {changedCoachName || '선택한'} 강사 소속으로 만들어져 그 강사의 수업 일정에서 사용합니다.
                                                    </span>
                                                    <span style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.6 }}>
                                                        • {selectedRecipientName || '받는 회원'}님이 {changedCoachName || '선택한'} 강사와 연결되어 있지 않으면 자동으로 연결됩니다. 기존 담당 강사는 그대로 남습니다.
                                                    </span>
                                                    <span style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.6 }}>
                                                        • 판매담당자는 원본 수강권 값을 그대로 가져갑니다. 양도 수강권은 판매금액 0원이라 새 매출이 생기지 않습니다.
                                                    </span>
                                                    <span style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.6 }}>
                                                        • 수업료 정산 요율은 원본 수강권 값을 물려받습니다. 새 강사 기준으로 바꾸려면 양도 후 양도 수강권의 요율을 수정하세요.
                                                    </span>
                                                    <span style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.6 }}>
                                                        • {memberName || '보내는 회원'}님과 원본 수강권의 담당 강사는 바뀌지 않습니다.
                                                    </span>
                                                </Col>
                                            </Col>
                                        )}
                                    </Col>
                                )}
                            </Col>
                        )}
                    </Col>

                    <Col style={{ width: 340, gap: 18, padding: 18, borderRadius: 12, backgroundColor: '#F8FAFC', border: '1px solid #E5E7EB' }}>
                        <Col style={{ gap: 8 }}>
                            <div style={sectionTitleStyle}>{isSplit ? '3. 나눌 회차' : '3. 양도 회차'}</div>
                            <input
                                style={textInputStyle}
                                inputMode="numeric"
                                placeholder={isSplit ? '나눌 회차' : '양도할 회차'}
                                value={transferCount}
                                onChange={(e) => setTransferCount(e.target.value.replace(/[^0-9]/g, ''))}
                            />
                            {selectedOption && (
                                <Body3Regular style={{ color: isValidCount || !transferCount ? '#6B7280' : '#DC2626' }}>
                                    {isSplit
                                        ? `최대 ${selectedMaxTransferable}회까지 나눌 수 있어요.`
                                        : `최대 ${selectedMaxTransferable}회까지 양도할 수 있어요.`}
                                </Body3Regular>
                            )}
                            {selectedOption && selectedLimitReserved > 0 && (
                                <Body3Regular style={{ color: '#6B7280', lineHeight: 1.5 }}>
                                    잔여 {selectedOption.remainingCount}회 중 대기 예약 {selectedLimitReserved}건 몫은 남겨 둬요.
                                </Body3Regular>
                            )}
                        </Col>

                        <Col style={{ gap: 8 }}>
                            <div style={sectionTitleStyle}>회차 번호</div>
                            <Row style={{ gap: 8 }}>
                                {([
                                    { code: 'RESET' as const, label: '1회차부터' },
                                    { code: 'CONTINUE' as const, label: '이어받기' },
                                ]).map((item) => {
                                    const selected = policy === item.code;
                                    return (
                                        <button
                                            key={item.code}
                                            onClick={() => setPolicy(item.code)}
                                            style={{
                                                flex: 1,
                                                height: 36,
                                                borderRadius: 8,
                                                border: selected ? '1.5px solid #3B82F6' : '1px solid #E5E7EB',
                                                backgroundColor: selected ? '#EFF6FF' : '#FFFFFF',
                                                color: selected ? '#2563EB' : '#6B7280',
                                                fontSize: 14,
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                            }}
                                        >
                                            {item.label}
                                        </button>
                                    );
                                })}
                            </Row>
                            {selectedOption && isValidCount && (
                                <Body3Regular style={{ color: '#6B7280', lineHeight: 1.5 }}>
                                    생성 회차: {policy === 'CONTINUE'
                                        ? `${selectedOption.currentSession + 1}~${selectedOption.currentSession + countNumber}회차`
                                        : `1~${countNumber}회차`}
                                </Body3Regular>
                            )}
                        </Col>

                        <Col style={{ gap: 8 }}>
                            <div style={sectionTitleStyle}>메모</div>
                            <textarea
                                style={{ ...textInputStyle, height: 88, paddingTop: 10, resize: 'none' }}
                                value={memo}
                                onChange={(e) => setMemo(e.target.value)}
                                placeholder="선택 입력"
                            />
                        </Col>

                        <Col style={{ gap: 8, padding: 12, borderRadius: 10, backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
                            <Body2Regular style={{ fontWeight: 700, color: '#111827' }}>처리 요약</Body2Regular>
                            {splitActionLabel && (
                                <Body3Regular style={{ color: '#B45309', fontWeight: 700, lineHeight: 1.6 }}>
                                    {splitActionLabel}
                                </Body3Regular>
                            )}
                            <Body3Regular style={{ color: '#6B7280', lineHeight: 1.6 }}>
                                {isSplit
                                    ? '원본 수강권 회차를 줄이고, 같은 회원에게 고른 강사의 0원 수강권을 만들어요.'
                                    : '원본 수강권은 잔여 회차만 차감되고, 양수자에게 판매금액 0원의 독립 수강권이 생성됩니다.'}
                            </Body3Regular>
                            {selectedOption && isValidCount && (
                                <Body3Regular style={{ color: '#111827', lineHeight: 1.6 }}>
                                    원본 잔여: {selectedOption.remainingCount}회 → {selectedOption.remainingCount - countNumber}회
                                </Body3Regular>
                            )}
                            {selectedOption && isCoachSelectable && !isSplit && (
                                <Body3Regular style={{ color: changedCoachId ? '#B45309' : '#111827', lineHeight: 1.6 }}>
                                    담당 강사: {changedCoachId
                                        ? `${sourceCoachName || '원본 강사'} → ${changedCoachName || '선택한 강사'}`
                                        : `${sourceCoachName || '원본 강사'} (유지)`}
                                </Body3Regular>
                            )}
                            {selectedOption && !isCoachSelectable && (
                                <Body3Regular style={{ color: '#6B7280', lineHeight: 1.6 }}>
                                    그룹 수강권은 담당 강사 없이 양도됩니다.
                                </Body3Regular>
                            )}
                        </Col>
                    </Col>
                </Row>
                </fieldset>

                {((!manualRetry && !isSelectedSourceUnresolved && selectedReviewKind) || manualRetry?.origin === 'busy') && (
                    <Col
                        style={{
                            gap: 8,
                            padding: '12px 14px',
                            borderRadius: 10,
                            backgroundColor: '#FFFBEB',
                            border: '1px solid #FDE68A',
                        }}
                    >
                        <Body3Regular style={{ color: '#92400E', lineHeight: 1.6 }}>
                            {manualRetry?.origin === 'busy'
                                ? TRANSFER_REVIEW_BUSY_NOTICE
                                : selectedReviewKind === 'incomplete'
                                ? TRANSFER_REVIEW_INCOMPLETE_NOTICE
                                : selectedReviewKind === 'unconfirmed'
                                ? TRANSFER_REVIEW_UNCONFIRMED_NOTICE
                                : TRANSFER_REVIEW_UNKNOWN_NOTICE}
                        </Body3Regular>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={isReviewConfirmed}
                                onChange={(e) => setIsReviewConfirmed(e.target.checked)}
                            />
                            <Body3Regular style={{ color: '#111827', fontWeight: 700 }}>
                                회원의 수강권 목록을 확인했어요
                            </Body3Regular>
                        </label>
                    </Col>
                )}

                <Row style={{ justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
                    {isRetryingSameKey && (
                        <Body3Regular style={{ color: '#6B7280' }}>
                            응답이 늦어 같은 요청으로 결과를 다시 확인하고 있어요.
                        </Body3Regular>
                    )}
                    {!isRetryingSameKey && manualRetry && (
                        <Body3Regular style={{ color: '#B45309' }}>
                            {manualRetry.origin === 'busy'
                                ? '상품 분류 적용이 끝나고 수강권 목록을 확인한 뒤 눌러 주세요. 같은 요청으로 다시 보내요.'
                                : '잔여 회차가 줄지 않았을 때만 눌러 주세요. 같은 요청으로 한 번 더 확인해요.'}
                        </Body3Regular>
                    )}
                    {!isRetryingSameKey && !manualRetry && isSelectedSourceUnresolved && (
                        <Body3Regular style={{ color: '#DC2626' }}>
                            결과를 확인하지 못한 요청이 있어 이 수강권은 다시 보낼 수 없어요.
                        </Body3Regular>
                    )}
                    {!isRetryingSameKey && !manualRetry && !isSelectedSourceUnresolved && isReviewPending && (
                        <Body3Regular style={{ color: '#DC2626' }}>
                            {selectedReviewKind === 'incomplete'
                                ? TICKET_TRANSFER_ROLLBACK_INCOMPLETE_BLOCK_MESSAGE
                                : TRANSFER_REVIEW_REQUIRED_MESSAGE}
                        </Body3Regular>
                    )}
                    <WhiteSquareButton text="취소" size="small" callBack={() => setIsVisible(false)} />
                    <ColorSquareButton
                        text={
                            isRetryingSameKey
                                ? `확인 중 ${sameKeyRetryCount}/${MAX_SAME_KEY_RETRIES}`
                                : manualRetry && !transferMutation.isLoading
                                ? '같은 요청 다시 확인'
                                : transferMutation.isLoading
                                ? '처리중'
                                : isSplit
                                ? isValidCount
                                    ? `${countNumber}회 나누기`
                                    : '나누기'
                                : '양도'
                        }
                        size="small"
                        customStyle={manualRetry ? { width: 150 } : undefined}
                        isDisabled={!canSubmit}
                        callBack={handleTransfer}
                    />
                </Row>
            </Col>
        </Modal>
    );
};

export default ModalTicketTransfer;
